// C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\MerchantUploads.tsx

import React, { useEffect, useMemo, useState } from "react";
import MerchantLayout from "../components/MerchantLayout";
import { apiGet, apiPost } from "../api";

type UploadKey =
  | "kessansho_main"
  | "kakutei_shinkoku"
  | "touhon"
  | "doctor_license"
  | "kaisetsu_todoke"
  | "kessansho_related"
  | "business_plan"
  | "other_1"
  | "other_2"
  | "other_3"
  | "other_4"
  | "other_5";


type UploadItem = {
  key: UploadKey;
  label: string;
  required: boolean;
  allowed: string;
  note?: string;
  isOther?: boolean;
};

type StatusItem = {
  document_key: string;
  document_label: string;
  other_title: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
};

const ALL_KEYS: UploadKey[] = [
  "kessansho_main",
  "kakutei_shinkoku",
  "touhon",
  "doctor_license",
  "kaisetsu_todoke",
  "kessansho_related",
  "business_plan",
  "other_1",
  "other_2",
  "other_3",
  "other_4",
  "other_5",
];


function makeBoolMapFalse(): Record<UploadKey, boolean> {
  return ALL_KEYS.reduce((acc, k) => {
    acc[k] = false;
    return acc;
  }, {} as Record<UploadKey, boolean>);
}

function makeStringMapEmpty(): Record<UploadKey, string> {
  return ALL_KEYS.reduce((acc, k) => {
    acc[k] = "";
    return acc;
  }, {} as Record<UploadKey, string>);
}

function isUploadKey(v: string): v is UploadKey {
  return (ALL_KEYS as string[]).includes(v);
}

export default function MerchantUploads() {
  // ✅ Uploaded status + file names
  const [uploaded, setUploaded] = useState<Record<UploadKey, boolean>>(makeBoolMapFalse());
  const [fileNames, setFileNames] = useState<Record<UploadKey, string>>(makeStringMapEmpty());

  // ✅ “その他” title input
  const [otherTitles, setOtherTitles] = useState<Record<string, string>>({
    other_1: "",
    other_2: "",
    other_3: "",
    other_4: "",
    other_5: "",
  });

  // ✅ Merchant type gate
  const [merchantType, setMerchantType] = useState<"個人" | "法人" | "">("");
  const [businessType, setBusinessType] = useState<string>("");

  const [typeError, setTypeError] = useState<string>("");

  // ✅ Loading / error
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [pageError, setPageError] = useState<string>("");

  // ✅ Per-item uploading state
  const [uploading, setUploading] = useState<Record<UploadKey, boolean>>(
    ALL_KEYS.reduce((acc, k) => {
      acc[k] = false;
      return acc;
    }, {} as Record<UploadKey, boolean>)
  );

  const typeSelected = merchantType === "個人" || merchantType === "法人";

  const items: UploadItem[] = useMemo(() => {
  // If merchant_type not selected, DO NOT show upload items
  if (!typeSelected) return [];

  const isBeautyMedical = (businessType || "").trim() === "美容医療";

  // Labels depend on merchant_type
  const baseRequired: UploadItem[] =
    merchantType === "個人"
  ? [
      { key: "kakutei_shinkoku", label: "確定申告（直近3年分）", required: true, allowed: "PDF / JPG" },
    ]

      : [
          { key: "kessansho_main", label: "決算書（直近3期分）", required: true, allowed: "PDF / JPG" },
          { key: "touhon", label: "謄本（履歴事項全部証明書）", required: true, allowed: "PDF / JPG" },
        ];

  const beautyRequired: UploadItem[] = isBeautyMedical
    ? [
        {
          key: "doctor_license",
          label: merchantType === "個人" ? "院長医師免許証" : "理事長医師免許証",
          required: true,
          allowed: "PDF / JPG",
          note: "※ 美容医療のみ",
        },
        {
          key: "kaisetsu_todoke",
          label: "開設届",
          required: true,
          allowed: "PDF / JPG",
          note: "※ 美容医療のみ",
        },
      ]
    : [];

  const optional: UploadItem[] = [
    {
      key: "kessansho_related",
      label: "決算書（運営会社・MS法人・関連会社）",
      required: false,
      allowed: "PDF / JPG",
    },
    { key: "business_plan", label: "事業計画書", required: false, allowed: "PDF / JPG / Excel / Word" },

    { key: "other_1", label: "その他①", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
    { key: "other_2", label: "その他②", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
    { key: "other_3", label: "その他③", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
    { key: "other_4", label: "その他④", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
    { key: "other_5", label: "その他⑤", required: false, allowed: "PDF / JPG / Excel / Word", isOther: true },
  ];

  return [...baseRequired, ...beautyRequired, ...optional];
}, [typeSelected, merchantType, businessType]);


  // ✅ On page open: load saved status + merchant type
  useEffect(() => {
    let alive = true;

    async function loadStatus() {
      setInitialLoading(true);
      setPageError("");
      setTypeError("");

      try {
        // 1) Get merchant info (for merchant_type)
        const me = await apiGet("/merchant/me");
        const mt = (me?.merchant?.merchant_type || "") as any;
const bt = (me?.merchant?.business_type || "") as any;

if (alive) {
  setMerchantType(mt === "個人" || mt === "法人" ? mt : "");
  setBusinessType(typeof bt === "string" ? bt : "");
}


        // 2) Get uploaded status
        const data = await apiGet("/merchant/documents/status");
        const rows: StatusItem[] = Array.isArray(data?.items) ? data.items : [];

        const nextUploaded = makeBoolMapFalse();
        const nextFileNames = makeStringMapEmpty();

        const nextOtherTitles: Record<string, string> = {
          other_1: "",
          other_2: "",
          other_3: "",
          other_4: "",
          other_5: "",
        };

        for (const r of rows) {
  const rawKey = r?.document_key || "";
  const rawLabel = r?.document_label || "";

  // ✅ Backward compat:
  // Old 個人 data was saved as kessansho_main with "確定申告書..." label.
  // Treat it as kakutei_shinkoku in the UI.
  const mappedKey =
    merchantType === "個人" && rawKey === "kessansho_main" && rawLabel.includes("確定申告")
      ? "kakutei_shinkoku"
      : rawKey;

  if (!isUploadKey(mappedKey)) continue;

  nextUploaded[mappedKey] = true;
  nextFileNames[mappedKey] = r?.file_name || "";

  if (mappedKey.startsWith("other_")) {
    nextOtherTitles[mappedKey] = r?.other_title || "";
  }
}


        if (!alive) return;

        setUploaded(nextUploaded);
        setFileNames(nextFileNames);
        setOtherTitles((prev) => ({ ...prev, ...nextOtherTitles }));
      } catch (e: any) {
        if (!alive) return;
        setPageError(e?.message || "書類状態の取得に失敗しました。");
      } finally {
        if (!alive) return;
        setInitialLoading(false);
      }
    }

    loadStatus();

    return () => {
      alive = false;
    };
  }, []);

  // ✅ On file select: upload to backend + save DB
  const onPickFile = async (it: UploadItem, file: File | null) => {
    if (!file) return;

    // ✅ NEW: must select merchant type first
    if (!typeSelected) {
      setTypeError("区分（個人／法人）を選択してください。未選択の場合は書類アップロードができません。");
      return;
    }

    // No replacement after upload (existing rule)
    if (uploaded[it.key]) return;

    setPageError("");
    setTypeError("");
    setUploading((p) => ({ ...p, [it.key]: true }));

    try {
      const fd = new FormData();
      fd.append("document_key", it.key);
      fd.append("document_label", it.label);
      fd.append("other_title", it.isOther ? (otherTitles[it.key] || "") : "");
      fd.append("upload_file", file);

      const res = await apiPost("/merchant/documents/upload", fd);

      const savedName = (res?.file_name || file.name) as string;

      setUploaded((prev) => ({ ...prev, [it.key]: true }));
      setFileNames((prev) => ({ ...prev, [it.key]: savedName }));

      if (it.isOther) {
        setOtherTitles((p) => ({ ...p, [it.key]: otherTitles[it.key] || "" }));
      }
    } catch (e: any) {
      setPageError(e?.message || "アップロードに失敗しました。");
    } finally {
      setUploading((p) => ({ ...p, [it.key]: false }));
    }
  };

  return (
    <MerchantLayout title="書類アップロード" active="uploads">
      <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>書類アップロード</div>

      <div style={{ color: "#475569", marginBottom: 12 }}>
        必要書類をアップロードしてください。必須書類がすべて提出済みになると完了になります。
      </div>

      {/* ✅ NEW: warning in this page when merchant_type not selected */}
      {!typeSelected && (
        <div
          style={{
            marginBottom: 14,
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            color: "#92400e",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          ⚠ 区分（個人／法人）を選択してください。未選択の場合は書類アップロードができません。
        </div>
      )}

      {/* ✅ Inline error box (Japanese) */}
      {pageError && (
        <div
          style={{
            marginBottom: 14,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#9f1239",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {pageError}
        </div>
      )}

      {/* ✅ Inline type error (when user clicks upload without selecting) */}
      {typeError && (
        <div
          style={{
            marginBottom: 14,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#9f1239",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {typeError}
        </div>
      )}

      <div
        style={{
          marginBottom: 16,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1e3a8a",
          padding: "10px 12px",
          borderRadius: 12,
          fontWeight: 900,
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        提出後は書類の差し替えができません。修正が必要な場合はJMCスタッフへご連絡ください。
      </div>

      {/* ✅ Initial loading indicator */}
      {initialLoading ? (
        <div
          style={{
            padding: "12px 0",
            color: "#334155",
            fontWeight: 900,
          }}
        >
          読み込み中...
        </div>
      ) : !typeSelected ? null : (

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          {items.map((it) => {
            const done = uploaded[it.key];
            const isUploading = uploading[it.key];

            const uploadDisabled = !typeSelected || done || isUploading;

            return (
              <div
                key={it.key}
                style={{
                  textAlign: "left",
                  padding: "14px 14px",
                  borderRadius: 14,
                  border: done ? "2px solid #16a34a" : "1px solid #cbd5e1",
                  background: done ? "#ecfdf5" : "#f8fafc",
                  opacity: isUploading ? 0.85 : !typeSelected ? 0.75 : 1,
                }}
              >
                {/* Title + Status */}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontWeight: 900, color: "#0f172a" }}>{it.label}</div>

                  <div
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 900,
                      background: done ? "#16a34a" : "#64748b",
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {done ? "提出済" : "未提出"}
                  </div>
                </div>

                {/* Meta */}
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: "#64748b",
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      padding: "3px 10px",
                      borderRadius: 999,
                      fontWeight: 900,
                      background: it.required ? "#ffe7e7" : "#eef3ff",
                      color: it.required ? "#b42318" : "#1d4ed8",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    {it.required ? "必須" : "任意"}
                  </span>

                  <span style={{ fontWeight: 800 }}>形式：{it.allowed}</span>
                  {it.note && <span style={{ fontWeight: 800 }}>{it.note}</span>}
                </div>

                {/* ✅ “その他” title input */}
                {it.isOther && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                      書類名（任意）
                    </div>
                    <input
                      value={otherTitles[it.key] || ""}
                      onChange={(e) => setOtherTitles((p) => ({ ...p, [it.key]: e.target.value }))}
                      placeholder="例：賃貸契約書 / 会社案内 / 見積書 など"
                      disabled={done || isUploading || !typeSelected}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid #cbd5e1",
                        outline: "none",
                        fontWeight: 700,
                        background: done || !typeSelected ? "#f1f5f9" : "#fff",
                        cursor: done || !typeSelected ? "not-allowed" : "text",
                      }}
                    />
                  </div>
                )}

                {/* File picker */}
                <div style={{ marginTop: 12 }}>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 16px",
                      borderRadius: 12,
                      border: done ? "1px solid #16a34a" : "1px solid #1d4ed8",
                      background: uploadDisabled
                        ? "linear-gradient(90deg, #94a3b8 0%, #64748b 100%)"
                        : done
                        ? "linear-gradient(90deg, #16a34a 0%, #15803d 100%)"
                        : "linear-gradient(90deg, #2e5f9e 0%, #1d4ed8 100%)",
                      color: "#fff",
                      fontWeight: 900,
                      cursor: uploadDisabled ? "not-allowed" : "pointer",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                      opacity: uploadDisabled ? 0.85 : 1,
                    }}
                    title={
                      !typeSelected
                        ? "区分（個人／法人）を選択してください"
                        : done
                        ? "提出済みのため差し替えできません"
                        : "ファイルを選択してアップロードします"
                    }
                  >
                    {isUploading ? "アップロード中..." : done ? "提出済み" : !typeSelected ? "区分を選択" : "アップロード"}

                    <input
                      type="file"
                      style={{ display: "none" }}
                      disabled={uploadDisabled}
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        e.currentTarget.value = "";
                        onPickFile(it, f);
                      }}
                    />
                  </label>

                  {fileNames[it.key] && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#334155", fontWeight: 800 }}>
                      提出済み：{fileNames[it.key]}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 14, color: "#6b7280", fontSize: 12, lineHeight: 1.7 }}>
        ※ 提出済みの状態はバックエンドに保存され、再ログイン後も保持されます。
      </div>
    </MerchantLayout>
  );
}
